import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import userService from '../services/userService';
import { UserAccount } from './models/user-account.model';
import * as uiSlice from './ui.slice';
import { PagedList } from './models/paged-list.model';
import { SortModel } from './models/sort-model.model';
import { UserCategoryTypeBC } from './models/user-category-type.model';

interface UserState {
  loggedInUserInfo: any | null;
  loadingLoggedInUserInfo: boolean;
  loadedLoggedInUserInfo: boolean;
  loggedInUserNoAccess: boolean;
  loggedInUserInitialized: boolean;
  userInfo: any | null;
  userApiInfo: any;
  loadingUserInfo: boolean;
  loadedUserInfo: boolean;
  userAccounts: PagedList<UserAccount>;
  onboardingUserAccounts: PagedList<UserAccount>;
  loadingUserAccounts: boolean;
  loadedUserAccounts: boolean;
  savingUser: boolean;
  savedUser: boolean;
  deletingUser: boolean;
  deletedUser: boolean;
  valdiatingEmail: string;
  loadingEmailValidation: boolean;
  emailValidation: { email: string; unique: boolean; valid: boolean };
  emailVerificationSuccess: boolean;
}

export const initialState: UserState = {
  loggedInUserInfo: null,
  loadingLoggedInUserInfo: false,
  loadedLoggedInUserInfo: false,
  loggedInUserNoAccess: false,
  loggedInUserInitialized: false,
  userInfo: null,
  userApiInfo: null,
  loadingUserInfo: false,
  loadedUserInfo: false,
  userAccounts: { itemCount: 0, currentPage: 1, totalPages: 1, items: [] },
  onboardingUserAccounts: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 1,
    items: [],
  },
  loadingUserAccounts: false,
  loadedUserAccounts: false,
  savingUser: false,
  savedUser: false,
  deletingUser: false,
  deletedUser: false,
  valdiatingEmail: '',
  loadingEmailValidation: false,
  emailValidation: { email: '', unique: true, valid: true },
  emailVerificationSuccess: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoggedInUserInfo: (state, action: PayloadAction<any>) => {
      state.loggedInUserInfo = action.payload;
    },
    setLoadingLoggedInUserInfo: (state, action: PayloadAction<boolean>) => {
      state.loadingLoggedInUserInfo = action.payload;
    },
    setLoadedLoggedInUserInfo: (state, action: PayloadAction<boolean>) => {
      state.loadedLoggedInUserInfo = action.payload;
    },
    setLoggedInUserNoAccess: (state, action: PayloadAction<boolean>) => {
      state.loggedInUserNoAccess = action.payload;
    },
    setLoggedInUserInitialized: (state, action: PayloadAction<boolean>) => {
      state.loggedInUserInitialized = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<any>) => {
      state.userInfo = action.payload;
    },
    setUserApiInfo: (state, action: PayloadAction<object>) => {
      state.userApiInfo = action.payload;
    },
    setLoadingUserInfo: (state, action: PayloadAction<boolean>) => {
      state.loadingUserInfo = action.payload;
    },
    setLoadedUserInfo: (state, action: PayloadAction<boolean>) => {
      state.loadedUserInfo = action.payload;
    },
    resetUserState: (state) => {
      return initialState;
    },
    setUserAccounts: (state, action: PayloadAction<PagedList<UserAccount>>) => {
      state.userAccounts = action.payload;
    },
    setOnboardingUserAccounts: (
      state,
      action: PayloadAction<PagedList<UserAccount>>,
    ) => {
      state.onboardingUserAccounts = action.payload;
    },
    setLoadingUserAccounts: (state, action: PayloadAction<boolean>) => {
      state.loadingUserAccounts = action.payload;
    },
    setLoadedUserAccounts: (state, action: PayloadAction<boolean>) => {
      state.loadedUserAccounts = action.payload;
    },
    savingUser: (state, action: PayloadAction<boolean>) => {
      state.savingUser = action.payload;
    },
    savedUser: (state, action: PayloadAction<boolean>) => {
      state.savedUser = action.payload;
    },
    deletingUser: (state, action: PayloadAction<boolean>) => {
      state.deletingUser = action.payload;
    },
    deletedUser: (state, action: PayloadAction<boolean>) => {
      state.deletedUser = action.payload;
    },
    addUser: (state, { payload: newUser }: PayloadAction<UserAccount>) => {
      state.onboardingUserAccounts.items.unshift(newUser);
      state.onboardingUserAccounts.itemCount += 1;
    },
    updateUser: (
      state,
      { payload: updatedUser }: PayloadAction<UserAccount>,
    ) => {
      const id = state.userAccounts.items.findIndex(
        (a) => a.userAccountID === updatedUser.userAccountID,
      );
      if (id >= 0) {
        state.userAccounts.items.splice(id, 1, updatedUser);
      } else {
        const id = state.onboardingUserAccounts.items.findIndex(
          (a) => a.userAccountID === updatedUser.userAccountID,
        );
        if (id >= 0) {
          state.onboardingUserAccounts.items.splice(id, 1, updatedUser);
        }
      }
    },
    deleteUser: (state, { payload: userId }: PayloadAction<number>) => {
      let id = state.userAccounts.items.findIndex(
        (a) => a.userAccountID === userId,
      );
      if (id >= 0) {
        state.userAccounts.items.splice(id, 1);
        state.userAccounts.itemCount -= 1;
      } else {
        id = state.onboardingUserAccounts.items.findIndex(
          (a) => a.userAccountID === userId,
        );
        if (id >= 0) {
          state.onboardingUserAccounts.items.splice(id, 1);
          state.onboardingUserAccounts.itemCount -= 1;
        }
      }
    },
    setValidatingEmail: (state, { payload: email }: PayloadAction<string>) => {
      state.valdiatingEmail = email;
    },
    setLoadingEmailValidation: (state, { payload: boolVal }) => {
      state.loadingEmailValidation = boolVal;
    },
    setEmailValidation: (
      state,
      { payload: emailValidation }: PayloadAction<any>,
    ) => {
      state.emailValidation = emailValidation;
    },
    setEmailVerificationSuccess: (state, action: PayloadAction<boolean>) => {
      state.emailVerificationSuccess = action.payload;
    },
  },
});

export const {
  setLoggedInUserInfo,
  setLoadingLoggedInUserInfo,
  setLoadedLoggedInUserInfo,
  setLoggedInUserNoAccess,
  setLoggedInUserInitialized,
  setUserInfo,
  setUserApiInfo,
  setLoadingUserInfo,
  setLoadedUserInfo,
  resetUserState,
  setUserAccounts,
  setOnboardingUserAccounts,
  setLoadingUserAccounts,
  setLoadedUserAccounts,
  savingUser,
  savedUser,
  deletingUser,
  deletedUser,
  addUser,
  updateUser,
  deleteUser,
  setValidatingEmail,
  setLoadingEmailValidation,
  setEmailValidation,
  setEmailVerificationSuccess,
} = userSlice.actions;

export const fetchLoggedInUserInfoAsync = () => (dispatch: Function) => {
  dispatch(setLoadingLoggedInUserInfo(true));
  dispatch(setLoadedLoggedInUserInfo(false));
  userService.getUserInfo().subscribe({
    next: (userInfo) => {
      dispatch(setLoggedInUserInfo(userInfo));
      dispatch(setLoadingLoggedInUserInfo(false));
      dispatch(setLoadedLoggedInUserInfo(true));
      dispatch(setLoggedInUserInitialized(true));
    },
    error: (err) => {
      // Checking for this specific error is currently the only way we have
      // of finding out a federated user is not verifed and assigned to
      // a Passenger. If this is the case, we call set the flag that
      // says the user is loaded and store an empty user object which
      // is used later to determine that this user needs to be verifed.
      // It feels pretty fragile but that is the way the back end is
      // implemented.
      if (err.message === 'No Identity Access record can be found.') {
        dispatch(setLoggedInUserInfo({}));
        dispatch(setLoadingLoggedInUserInfo(false));
        dispatch(setLoadedLoggedInUserInfo(true));
      } else {
        dispatch(setLoggedInUserNoAccess(true));
        dispatch(setLoadingLoggedInUserInfo(false));
        dispatch(setLoadedLoggedInUserInfo(false));
      }
      dispatch(setLoggedInUserInitialized(true));
    },
  });
};

export const fetchUserInfoAsync = (id?: number) => (dispatch: Function) => {
  dispatch(setLoadingUserInfo(true));
  dispatch(setLoadedUserInfo(false));
  userService.getUserInfo(id).subscribe({
    next: (userInfo) => {
      dispatch(setUserInfo(userInfo));
      dispatch(setLoadingUserInfo(false));
      dispatch(setLoadedUserInfo(true));
    },
    error: (err) => {
      dispatch(setLoadingUserInfo(false));
      dispatch(setLoadedUserInfo(false));
    },
  });
};

export const fetchUserApiInfoAsync = () => (dispatch: Function) => {
  userService.getUserApiInfo().subscribe({
    next: (apiInfo: any) => {
      dispatch(setUserApiInfo(apiInfo));
    },
    error: (err) => {
      uiSlice.setErrorMessage(err.message || 'snackbar.apiVersionError');
    },
  });
};

export const fetchUserAccountsAsync =
  (
    pageSize: number,
    page: number,
    search: string,
    sortModel: SortModel | null,
    includeRoles: boolean,
    roleIDs?: number[],
  ) =>
  (dispatch: any) => {
    dispatch(
      setUserAccounts({
        itemCount: 0,
        currentPage: 1,
        totalPages: 1,
        items: [],
      }),
    );
    dispatch(setLoadingUserAccounts(true));
    dispatch(setLoadedUserAccounts(false));
    userService
      .findUsers(
        pageSize,
        page,
        search,
        sortModel,
        includeRoles,
        false,
        roleIDs,
      )
      .subscribe({
        next: (response: PagedList<UserAccount>) => {
          dispatch(setUserAccounts(response));
          dispatch(setLoadingUserAccounts(false));
          dispatch(setLoadedUserAccounts(true));
        },
        error: (err: any) => {
          dispatch(setLoadingUserAccounts(false));
          dispatch(setLoadedUserAccounts(false));
        },
      });
  };

export const fetchOnboardingUserAccountsAsync =
  (
    pageSize: number,
    page: number,
    search: string,
    sortModel: SortModel | null,
    includeRoles: boolean,
  ) =>
  (dispatch: any) => {
    dispatch(
      setOnboardingUserAccounts({
        itemCount: 0,
        currentPage: 1,
        totalPages: 1,
        items: [],
      }),
    );
    dispatch(setLoadingUserAccounts(true));
    dispatch(setLoadedUserAccounts(false));
    userService
      .findUsers(pageSize, page, search, sortModel, includeRoles, true)
      .subscribe({
        next: (response: PagedList<UserAccount>) => {
          dispatch(setOnboardingUserAccounts(response));
          dispatch(setLoadingUserAccounts(false));
          dispatch(setLoadedUserAccounts(true));
        },
        error: (err: any) => {
          dispatch(setLoadingUserAccounts(false));
          dispatch(setLoadedUserAccounts(false));
        },
      });
  };

export const createUserAsync = (userData: UserAccount) => (dispatch: any) => {
  dispatch(savingUser(true));
  userService.createUser(userData).subscribe({
    next: (newUser) => {
      // mark password as temporary so that the user won't show up as active in users list
      newUser.isPasswordTemporary = true;
      dispatch(addUser(newUser));
      dispatch(savedUser(true));
      dispatch(savingUser(false));
      dispatch(uiSlice.setSuccessMessage('snackbar.createUserSuccess'));
    },
    error: (err) => {
      dispatch(savedUser(false));
      dispatch(savingUser(false));
      dispatch(
        uiSlice.setErrorMessage(err.message || 'snackbar.createUserFailed'),
      );
    },
  });
};

export const updateUserAsync = (userData: any) => (dispatch: any) => {
  dispatch(savingUser(true));
  userService.updateUser(userData).subscribe({
    next: (updatedUser) => {
      dispatch(updateUser(updatedUser));
      dispatch(savedUser(true));
      dispatch(savingUser(false));
      dispatch(uiSlice.setSuccessMessage('snackbar.updateUserSuccess'));
    },
    error: (err) => {
      dispatch(savedUser(false));
      dispatch(savingUser(false));
      dispatch(
        uiSlice.setErrorMessage(err.message || 'snackbar.updateUserFailed'),
      );
    },
  });
};

export const deleteUserAsync =
  (userId: number, userCategoryTypeBC: UserCategoryTypeBC) =>
  (dispatch: any) => {
    dispatch(deletedUser(false));
    dispatch(deletingUser(true));
    let serviceFn = userService.deleteUser;
    if (userCategoryTypeBC === UserCategoryTypeBC.Passenger) {
      serviceFn = userService.deleteMemberUser;
    } else if (userCategoryTypeBC === UserCategoryTypeBC.Client) {
      serviceFn = userService.deleteClientUser;
    }

    serviceFn(userId).subscribe({
      next: () => {
        dispatch(deleteUser(userId));
        dispatch(deletedUser(true));
        dispatch(deletingUser(false));
        dispatch(uiSlice.setSuccessMessage('snackbar.deleteUserSuccess'));
      },
      error: (err) => {
        dispatch(deletingUser(false));
        dispatch(
          uiSlice.setErrorMessage(err.message || 'snackbar.deletUserFailed'),
        );
      },
    });
  };

export const sendPasswordResetAsync =
  (userId: number, email: string) => (dispatch: Function) => {
    userService.sendPasswordResetEmail(userId).subscribe({
      next() {
        dispatch(uiSlice.setSuccessMessage('snackbar.resetPasswordSent'));
      },
      error(err: any) {
        dispatch(uiSlice.setErrorMessage('snackbar.resetPasswordFailed'));
      },
    });
  };

export const sendAccountActivationAsync =
  (userId: number, email: string) => (dispatch: Function) => {
    userService.sendAccountActivationEmail(userId).subscribe({
      next() {
        dispatch(uiSlice.setSuccessMessage('snackbar.activateAccountSent'));
      },
      error(err: any) {
        dispatch(uiSlice.setErrorMessage('snackbar.activateAccountFailed'));
      },
    });
  };

export const validateUniqueEmailAsync =
  (email: string) => (dispatch: Function) => {
    dispatch(setLoadingEmailValidation(true));
    dispatch(setValidatingEmail(email));
    dispatch(setEmailValidation({ email, unique: true, valid: true }));
    userService.validateUniqueEmail(email).subscribe({
      next: (result) => {
        dispatch(setEmailValidation(result));
      },
      error: (err) => {
        // This API shouldn't throw errors but right now it is so this needs to catch that
        const valid = err.message !== 'Invalid email format.';
        const unique = !valid;
        dispatch(setEmailValidation({ email, unique, valid }));
      },
      complete: () => {
        dispatch(setLoadingEmailValidation(false));
      },
    });
  };

export const createClientUserAsync =
  (clientBC: string, userData: Record<string, any>) => (dispatch: any) => {
    dispatch(savingUser(true));
    dispatch(savedUser(false));

    userService.createClientPortalUser(clientBC, userData).subscribe({
      next: (newUser) => {
        dispatch(addUser(newUser));
        dispatch(savedUser(true));
        dispatch(savingUser(false));
        dispatch(uiSlice.setSuccessMessage('snackbar.createUserSuccess'));
      },
      error: (err) => {
        dispatch(savedUser(false));
        dispatch(savingUser(false));
        dispatch(
          uiSlice.setErrorMessage(err.message || 'snackbar.createUserFailed'),
        );
      },
    });
  };

export const updateClientUserAsync =
  (userAccountID: number, userData: any) => (dispatch: any) => {
    dispatch(savingUser(true));
    dispatch(savedUser(false));

    userService.updateClientPortalUser(userAccountID, userData).subscribe({
      next: (newUser) => {
        dispatch(updateUser(newUser));
        dispatch(savedUser(true));
        dispatch(savingUser(false));
        dispatch(uiSlice.setSuccessMessage('snackbar.updateUserSuccess'));
      },
      error: (err) => {
        dispatch(savedUser(false));
        dispatch(savingUser(false));
        dispatch(
          uiSlice.setErrorMessage(err.message || 'snackbar.updateUserFailed'),
        );
      },
    });
  };

export const selectLoggedInUserInfo = (state: { user: UserState }) =>
  state.user?.loggedInUserInfo;
export const selectLoadingLoggedInUserInfo = (state: { user: UserState }) =>
  state.user.loadingLoggedInUserInfo;
export const selectLoadedLoggedInUserInfo = (state: { user: UserState }) =>
  state.user.loadedLoggedInUserInfo;
export const selectLoggedInUserNoAccess = (state: { user: UserState }) =>
  state.user.loggedInUserNoAccess;
export const selectLoggedInUserInitialized = (state: { user: UserState }) =>
  state.user.loggedInUserInitialized;
export const selectUserInfo = (state: { user: UserState }) =>
  state.user.userInfo;
export const selectLoadingUserInfo = (state: { user: UserState }) =>
  state.user.loadingUserInfo;
export const selectLoadedUserInfo = (state: { user: UserState }) =>
  state.user.loadedUserInfo;
export const selectUserAccounts = (state: { user: UserState }) =>
  state.user.userAccounts;
export const selectOnboardingUserAccounts = (state: { user: UserState }) =>
  state.user.onboardingUserAccounts;
export const selectLoadingUserAccounts = (state: { user: UserState }) =>
  state.user.loadingUserAccounts;
export const selectLoadedUserAccounts = (state: { user: UserState }) =>
  state.user.loadedUserAccounts;
export const selectSavingUser = (state: { user: UserState }) =>
  state.user.savingUser;
export const selectSavedUser = (state: { user: UserState }) =>
  state.user.savedUser;
export const selectDeletingUser = (state: { user: UserState }) =>
  state.user.deletingUser;
export const selectDeletedUser = (state: { user: UserState }) =>
  state.user.deletedUser;
export const selectEmailValidation = (state: { user: UserState }) =>
  state.user.emailValidation;
export const selectLoadingEmailValidation = (state: { user: UserState }) =>
  state.user.loadingEmailValidation;
export const selectUserApiInfo = (state: { user: UserState }) =>
  state.user?.userApiInfo;
export const selectEmailVerificationSuccess = (state: { user: UserState }) =>
  state.user.emailVerificationSuccess;

export default userSlice.reducer;
